import React, { FC, useContext, useState } from 'react';
import { Order, Sample } from 'models/order';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { sampleAndPackageTemplate } from 'apollo/templates/order';
import SampleMethod from 'components/lab-order/sample-method/sample-method.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useSeedOnOpen } from 'hooks/useSeedOnOpen';

type SampleMethodDialogProps = {
    onUpdate?: (order: Order) => void;
    onClose: () => void;
    open: boolean;
    order: Order;
};

const SampleMethodDialog: FC<SampleMethodDialogProps> = ({
    onUpdate,
    onClose,
    open,
    order,
}: SampleMethodDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [sample, setSample] = useState<Sample>({
        type: '',
        content: [],
        otherContent: '',
    });

    useSeedOnOpen(open, () => {
        if (order) {
            if (order.deliveryDetails?.sample) {
                setSample(order.deliveryDetails.sample);
            }
        }
    });

    const handleSample = (sample: Sample) => {
        setSample(sample);
    };

    const [sampleAndPackage] = useMutation(sampleAndPackageTemplate, {
        onError: (err) => {
            console.log('err', err);
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('orders:SAMPLE_NOT_UPDATED'),
            });
        },
        onCompleted: ({ sampleAndPackage }) => {
            sampleAndPackage.version = order.version + 1;
            onUpdate?.({ ...order, ...sampleAndPackage });
            onClose();
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('orders:SAMPLE_UPDATED'),
            });
        },
    });

    const handleSave = async () => {
        await sampleAndPackage({
            variables: {
                orderId: order?._id,
                sample: sample,
            },
        });
    };

    const handleClose = () => {
        if (order.deliveryDetails?.sample) {
            setSample(order.deliveryDetails.sample);
        }
        onClose();
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={onClose}
            title={translate('orders:SAMPLE_AND_PACKAGE_CONTENT')}
            actions={
                <Button onClick={handleSave} variant="contained" data-test="sample-method-dialog.save-btn">
                    {translate('common:SAVE')}
                </Button>
            }
        >
            <Box mb={3}>
                <SampleMethod
                    handleSampleSelection={handleSample}
                    sample={sample}
                    dataTest="sample-method-dialog.sample-method"
                />
            </Box>
        </DHDialog>
    );
};
export default SampleMethodDialog;
